import { gEnums } from 'global/enums/globalEnums';
import { setStyleAndClass } from 'global/styles/formatting';
import _ from 'lodash';
import { createContext, useContext, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { _checkComponentTimes } from '../../../App';
import { _projectAppNames, isLocalhost } from '../../../project/appConfiguration';
import FullPageAlert from '../../components/alerts/FullPageAlert';
import { currentHelpers } from '../../redirection/current';
import { eventSettings_functions, homeSettings_functions } from '../reducers/functionHelpers/settingsDataFunctions';
import { _cacheNames, _cacheTypes } from '../reducers/ServiceWorkerReducer';
import { AppSettingsContext } from './AppSettingsContext';
import { AuthContext } from './AuthContext';
import { PapsContext } from './PapsContext';
import { RegDataContext } from './RegDataContext';
import { _settingSourceTypes, _useCache } from './ServiceWorkerContext';
import { StartContext } from './StartContext';
import { useAppSettings } from './useHelpers/useAppSettings';
import { DemoContext } from './DemoContext';
import { useSettingsAreaSettings } from './useHelpers/useSettingsAreaSettings';
import { _hostNames, _hostPaths } from './useHelpers/useHost';

const _ignoreSa = false
const _reassigingToPojo = true

const _appUserStuff = [
  'appUserDataModifications',
  'appUserDataSettings',
  'appUserImageOptions',
  'appUserAuth',
  'appUserPagePermissions',
  'appUserPermissions',
  'appUserProfileInfo',
  'appUserSettings',
  'appUserUiOptions',
]


export const ClientSettingsContext = createContext();
export const EventSettingsContext = createContext();
export const HomeSettingsContext = createContext();

export const CurrentSettingsContext = createContext()

/**
 * Returns the home, client and event settings contexts from the database 
 * @param {object} props (ignoreStart)
 * @provides ClientSettingsContext (clientSettings)
 * @provides EventSettingsContext (eventSettings)
 * @provides HomeSettingsContext (homeSettings)
 * @contexts [StartContext, AuthContext, AppSettingContext, PapsProvider] 
 * @returns 
 */
const SettingsDataProvider = (props) => {

  const navigate = useNavigate()
  const { getSettingsAreaSettings } = useSettingsAreaSettings()

  const { combinedSettings } = useAppSettings()

  const _isLocalhost = isLocalhost()
  const _host_doc = document.location.host

  const authContext = useContext(AuthContext)
  const { auth_state } = authContext ?? {}
  const { isAppDeveloper } = auth_state ?? {}

  const _isSuperAdmin = _ignoreSa ? false : isAppDeveloper

  // startContext
  const startContext = useContext(StartContext);
  const { start_fns } = startContext ?? {}

  // papsContext
  const papsContext = useContext(PapsContext);
  const { paps_state } = papsContext ?? {}
  const { settingsDocName, pathViews, pathName, pathname, view, viewKey, pageKey, otherView, landingView } = paps_state ?? {}
  const { clients: client_key, events: event_key } = pathViews ?? {}

  let trueView = (viewKey || otherView) ? view : view + '_list'
  trueView = currentHelpers.getTrueView(pathViews, view, trueView, landingView, viewKey)

  const _isHome = !client_key && !event_key ? true : false
  const isClient = client_key ? true : false
  const isEvent = event_key ? true : false

  // appSettingsContext
  const appSettingsContext = useContext(AppSettingsContext);
  const { appSettings_state, appSettings_handlers } = appSettingsContext ?? {}
  const {
    appConfigSettings,
    appLanding_global,
    appSettings,
    appShortUrls_global,
    currentPathName,
    fbErrorCode,
    modifiedAppSettings,
  } = appSettings_state ?? {}

  const { useAppLanding, landingUrl, clientKey, eventKey, appLandingUrls } = appLanding_global ?? {}
  const { useAppShortUrls, appLandingShortUrls } = appShortUrls_global ?? {}
  const { appNames } = appConfigSettings ?? {}
  const { appName } = appNames ?? {}
  const { hceSettings, settingsReady, settingsError } = appSettings ?? {}

  const demoContext = useContext(DemoContext);
  const { demo_state } = demoContext ?? {}
  const { demoPreview, demoOn } = demo_state ?? {}
  const { projectSettings: projectSettings_demo } = demoPreview ?? {}

  const regDataContext = useContext(RegDataContext)
  const { regData_state } = regDataContext ?? {}
  const { isRegDemo, regInfo } = regData_state ?? {}
  const { projectSettings: projectSettings_reg } = regInfo ?? {}

  _checkComponentTimes && start_fns.useTimeMeasurement('SettingsDataProvider', settingsReady)

  let _clientKeySettings;
  let _eventKeySettings;
  let _regKeySettings;
  let _demoKeySettings;

  if (hceSettings) {
    _clientKeySettings = isClient ? hceSettings.client : null
    _eventKeySettings = isEvent ? hceSettings.event : null
    _regKeySettings = isEvent && isRegDemo ? projectSettings_reg : null
    _demoKeySettings = isEvent && demoOn ? projectSettings_demo : null
  }

  const newPath = !_.isEqual(currentPathName, pathname)

  // these are the setting from home, client and event
  const states_all = {
    home: hceSettings ? { homeSettings: hceSettings.home, clientSettings: hceSettings.client } : {},
    client: _clientKeySettings ? { clientSettings: _clientKeySettings } : {},
    event: _eventKeySettings ? { eventSettings: _eventKeySettings } : {},
    reg: _regKeySettings ? { eventSettings: _regKeySettings } : {},
    demo: _demoKeySettings ? { eventSettings: _demoKeySettings } : {},
  }


  useEffect(
    () => {
      // const hasSettings = _.some(combinedSettings, setting => !_.isEmpty(setting))
      // sets appSettings 
      appSettings_handlers.handleSet_hceSettings(combinedSettings)
    },
    // eslint-disable-next-line
    [settingsDocName, combinedSettings]
  )

  useEffect(() => {
    if (modifiedAppSettings && newPath) {
      appSettings_handlers.handleAmmend_modifiedAppSettings()
    }
  }, [newPath, modifiedAppSettings]);

  const _hostUrl = useMemo(() => {

    let result;

    if (!isClient) {
      if (useAppLanding && !_isSuperAdmin) {
        if (landingUrl) {
          result = '/' + landingUrl;
        } else if (clientKey && eventKey) {
          result = '/clients/' + clientKey + '/events/' + eventKey;
        } else if (appLandingUrls) {
          appLandingUrls.forEach(appLandingUrl => {
            const { host, hostUrl } = appLandingUrl;
            if (_host_doc === host) {
              if (hostUrl) {
                result = hostUrl;
              }
            }
          });
        }
      }

      if (useAppShortUrls && appLandingShortUrls && _isHome) {
        const splits = currentHelpers.getSplits();
        appLandingShortUrls.forEach(appLandingShortUrl => {
          const { host, shortUrl, redirectUrl } = appLandingShortUrl;
          if (_host_doc === host || _isLocalhost) {
            const shortMatch = splits.includes(shortUrl);
            if (shortMatch) {
              result = redirectUrl;
            }
          }
        });
      }

      if (_reassigingToPojo && _isHome && (_host_doc === _hostNames.thumbstat) && !_isSuperAdmin) {
        result = _hostPaths.thumstat
      }
    }

    return result;
  }, [
    useAppLanding,
    useAppShortUrls,
    clientKey,
    eventKey,
    appLandingUrls,
    appLandingShortUrls,
    _host_doc,
    _isSuperAdmin,
    _isHome,
    _reassigingToPojo,
    _isLocalhost,
    landingUrl,
  ]);

  // triggers the redirection of the app to the `landingUrl` if useAppLanding and `landingUrl` are set.
  useEffect(() => {
    if (_hostUrl) {
      navigate(_hostUrl);
    }
  }, [_hostUrl]); // Only navigate when _hostUrl changes

  const homeSettings_fns = useMemo(() => {
    return homeSettings_functions(states_all)
  }, [states_all])

  const eventSettings_fns = useMemo(() => {
    return eventSettings_functions(hceSettings)
  }, [hceSettings])

  const getLists = (viewItems) => {

    const viewItemsList = []
    const allPropsList = []

    if (viewItems) {
      Object.keys(viewItems).forEach(viKey => {
        if (!viewItemsList.includes(viKey)) { viewItemsList.push(viKey) }
        const gvi = viewItems[viKey]
        if (gvi && _.isObject(gvi)) {
          if (!gvi.key) { gvi.key = viKey }
          if (gvi.props) {
            Object.keys(gvi.props).forEach(prKey => {
              if (!allPropsList.includes(prKey)) { allPropsList.push(prKey) }
            })
          }
        }
      })
    }

    return {
      viewItemsList,
      allPropsList
    }
  }

  const currentSettings_state = useMemo(() => {

    if (!states_all) return;

    const hceStates = {
      homeSettings_state: states_all?.home,
      clientSettings_state: states_all?.client,
      eventSettings_state: states_all?.event,
      regSettings_state: states_all?.reg,
      demoSettings_state: states_all?.demo,
    }

    const homeClientEventProps = { pathViews, hceStates }
    const settingsAreaProps = getSettingsAreaSettings({ homeClientEventProps, isRegDemo, demoOn })
    const { settings_currentArea } = settingsAreaProps ?? {}
    const { global, views, viewItems } = settings_currentArea ?? {}

    const appUserSettings = {}
    let aps_styles = {}

    if (global) {
      _.forEach(global, (value, key) => {
        if (_appUserStuff.includes(key) && value) {
          _.assign(appUserSettings, value);
        }
      });
      aps_styles = _.mapValues(gEnums.projectStyles, (_, psKey) =>
        setStyleAndClass(global, psKey)
      );
    }
    const _lists = getLists(viewItems)
    const gvviSettings = { aps_global: global, aps_views: views, aps_viewItems: viewItems, aps_appUserSettings: appUserSettings, aps_styles, ..._lists }

    const _currss = {
      settingsAreaProps,
      gvviSettings,
      settingsSourceType: settingsAreaProps.settingsSourceType,
      settingsFor: pathName
    }

    return _currss

  }, [states_all, pathViews, isRegDemo, demoOn])

  // return <div className='cntr' style={{ color: 'white' }}>{'SETTINGS'}</div>

  const providerContext = () => <HomeSettingsContext.Provider value={{ homeSettings_state: states_all.home, homeSettings_fns }}>
    <ClientSettingsContext.Provider value={{ clientSettings_state: states_all.client }}>
      <EventSettingsContext.Provider value={{ eventSettings_fns, eventSettings_state: states_all.event }}>
        <CurrentSettingsContext.Provider value={{ currentSettings_state }}>
          {props.children}
        </CurrentSettingsContext.Provider>
      </EventSettingsContext.Provider>
    </ClientSettingsContext.Provider>
  </HomeSettingsContext.Provider>

  if (settingsError) {
    switch (appName) {
      case _projectAppNames.pojo:
        return <FullPageAlert
          title={'Thumbstat'}
          message={"Hello there! Currently working on improving your experience. We are in the process of creating a upgraded version due the site traffic. We apologize for any inconvenience this may cause. Thank you for your patience and understanding! Please try back again at a later time."}
        />
      default:
        return <FullPageAlert
          title={appName}
          message={fbErrorCode ? fbErrorCode : settingsError.toString()}
        />
    }
  } else {
    return providerContext()
  }

}

export default SettingsDataProvider